
import axios from 'axios';
import { fun } from '@/lib/request';
import { api } from '@/lib/env';
import { StateSlice } from '@/stores/appStore';
import { formatData, snakeToCamel, transferSnakeToCamel } from '@/lib/utils';
import { toast } from 'react-hot-toast';
import { PaperId } from './paperSlice';
import { SubjectId } from './subjectSlice';
import { WrapResponse } from '@/types';
import { NoteData, Note, Notes, NoteId, NoteType } from '@/stores/context/createNoteStore';
import { getFileHash } from '@/lib/getFileHash';

type Id = SubjectId | PaperId;
// 服务端返回的数据
type NotesData = {
  paperId: PaperId;
  paperName: string;
  title: string;
  rootId: string;
  notes: NoteData[];
};

function parseNoteJson<T>(value: string | undefined, fallback: T): T {
  try {
    return value ? JSON.parse(value) as T : fallback;
  } catch {
    return fallback;
  }
}

function toNote(data: NoteData): Note | null {
  const { noteId, pid, children = [], viewer, images, type, ...rest } = data;
  if (!noteId) return null;

  return {
    viewer: parseNoteJson(viewer, {}),
    images: parseNoteJson(images, []),
    type: transferSnakeToCamel(type) as NoteType,
    ...rest,
    pid: pid?.toString() || '',
    id: noteId.toString(),
    children: (children || []).map((childId) => childId.toString()),
  };
}

function toEntities(notes: NoteData[]): Record<NoteId, Note> {
  return notes.reduce<Record<NoteId, Note>>((entities, noteData) => {
    const note = toNote(noteData);
    if (note) {
      entities[note.id] = note;
    }
    return entities;
  }, {});
}
export type NoteEntity = {
  subjectId: SubjectId;
  paperId: PaperId;
  paperName: string;
  title: string;
} & Notes;

type NoteEntityInput = Pick<Notes, 'rootId' | 'entities'>;

type FileInfo = {
  id: string;
  md5: string;
  sha1: string;
};

export type MindSlice = {
  noteEntities: Record<Id, NoteEntity>,
  allPaperIds: PaperId[],
  fetchNoteLoading: boolean;
  fetchNotes: (id: Id, isSubject?: boolean) => Promise<boolean>;
  fetchAllNotes: (subjectId?: string) => Promise<boolean>;
  uploadImages: (files: Blob[], scene?: string) => Promise<string[]>;
  setNoteEntity: (id: Id, value: NoteEntityInput) => void;
};

export const createMindSlice: StateSlice<MindSlice> = (set, get) => {
  let latestFetchAllNotesRequestId = 0;

  return ({
    noteEntities: {},
    allPaperIds: [],
    fetchNoteLoading: false,
    fetchNotes: async (id, isSubject = false) => {
      set((state) => {
        state.fetchNoteLoading = true;
      });
      try {
        const formData = new FormData();
        isSubject ? (
          formData.append('subject_id', id)
        ) : (
          formData.append('paper_id', id)
        );
        const { data, code, message: msg } = (
          await fun.post<WrapResponse<NotesData>>(
            api('/note/list', true),
            formData,
          )
        ).data;
        if (code === 0) {
          const { rootId, notes, paperName, title } = snakeToCamel<NotesData>(data);
          set((state) => {
            const entities = toEntities(notes);
            state.noteEntities[id] = {
              entities,
              rootId: rootId.toString(),
              paperName,
              title,
              paperId: isSubject ? '' : id,
              subjectId: isSubject ? id : '',
            };
          });
          return true;
        } else {
          toast.error(msg);
          console.error('Error fetch notes:', msg);
        }
      } catch (error) {
        console.error('Error fetch notes:', error);
      } finally {
        set((state) => {
          state.fetchNoteLoading = false;
        });
      }
      return false;
    },
    fetchAllNotes: async (subjectId = '') => {
      const requestId = ++latestFetchAllNotesRequestId;
      set((state) => {
        state.fetchNoteLoading = true;
      });
      try {
        const formData = new FormData();
        subjectId && formData.append('subject_id', subjectId);
        const { data, code, message: msg } = (
          await fun.post<WrapResponse<NotesData[]>>(
            api('/note/listAll', true),
            formData,
          )
        ).data;
        if (code === 0) {
          const allNotesData = formatData<NotesData[]>(data);
          const allNotes: PaperId[] = [];

          if (requestId !== latestFetchAllNotesRequestId) {
            return false;
          }

          set((state) => {
            allNotesData.forEach(({ notes, paperId, paperName, title, rootId }) => {
              const entities = toEntities(notes);
              state.noteEntities[paperId] = {
                entities,
                rootId: rootId?.toString(),
                paperName,
                title,
                paperId: paperId.toString(),
                subjectId: '',
              }
              allNotes.push(paperId);
            });
            state.allPaperIds = allNotes;
          });
          return true;
        } else {
          toast.error(msg);
        }
        console.error('Error fetch notes:', msg);
      } catch (error) {
        console.error('Error fetch notes:', error);
      } finally {
        if (requestId === latestFetchAllNotesRequestId) {
          set((state) => {
            state.fetchNoteLoading = false;
          });
        }
      }
      return false;
    },
    setNoteEntity: (id, value) => {
      set((state) => {
        const prev = state.noteEntities[id];
        if (!prev) return;
        prev.entities = value.entities;
        prev.rootId = value.rootId;
      });
    },
    uploadImages: async (files, scene = 'note_img') => {
      const TOAST_ID = 'uploading'
      toast.loading('截图上传中', {
        id: TOAST_ID,
      });
      try {
        const filesInfo: FileInfo[] = [];
        for (const file of files) {
          const md5 = await getFileHash(file, 'MD5');
          const sha1 = await getFileHash(file, 'SHA1');
          const id = `${md5}${sha1}`;
          filesInfo.push({
            id,
            md5,
            sha1,
          });
        }
        const uploadFormData = new FormData();
        filesInfo.forEach((data, i) => {
          uploadFormData.append('scene', scene);
          uploadFormData.append(`files[${i}][md5]`, data.md5);
          uploadFormData.append(`files[${i}][sha1]`, data.sha1);
          uploadFormData.append(`files[${i}][content-type]`, 'image/png');
        })
        // 获取到可上传的到 oss 的签名 url
        const uploadData = (await fun.post<WrapResponse<string[]>>(
          api('/upload/getUploadUrl', true), uploadFormData
        )).data;
        // 检测
        if (uploadData.code === 0) {
          const uploads = uploadData.data || [];
          for (let index = 0; index < uploads.length; index++) {
            const uploadUrl = uploads[index];
            try {
              await axios.put<WrapResponse<{}>>(
                uploadUrl,
                files[index],
                {
                  headers: {
                    'Content-Type': 'image/png',
                  },
                }
              );
            } catch (error) {
              console.error('Error uploading file:', error);
            }
          }
          return uploads.map(url => url.split('?')[0]);
        } else {
          toast.error(uploadData.message);
          return [];
        }
      } catch (error) {
        console.error('Error get url file:', error);
        return [];
      } finally {
        toast.dismiss(TOAST_ID);
      }
    },
  });
}
